(() => {

  if (customElements.get('zardo-cart-discounts')) return;

  // The existing cart replaces footer markup; keep interaction state outside it.

  const state = { busy: false, open: false, input: '', message: '', error: false, codes: [], types: {}, loaded: false };

  const renderAll = () => document.querySelectorAll('zardo-cart-discounts').forEach(node => node.renderState());

  const acceptCart = cart => {

    if (!Array.isArray(cart?.discount_codes)) return;

    state.codes = cart.discount_codes.filter(item => item.applicable).map(item => item.code);

    state.types = Object.fromEntries([

      ...(cart.cart_level_discount_applications || []),

      ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))

    ].filter(Boolean).map(app => [app.title, app.type]));

    state.loaded = true;

    renderAll();

  };

  let metadataRequest;

  const loadMetadata = () => {

    if (state.loaded || state.busy || metadataRequest) return;

    metadataRequest = fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { cache: 'no-store' })

      .then(response => response.ok ? response.json() : null).then(acceptCart)

      .catch(() => {}).finally(() => { metadataRequest = null; });

  };

  document.addEventListener('zardo:cart:upsell-added', event => acceptCart(event.detail?.cart));

  document.addEventListener('cart:change', event => acceptCart(event.detail?.cart));

  class CartDiscounts extends HTMLElement {

    connectedCallback() {

      if (this.ready) return;

      this.ready = true;

      this.renderState();

      this.visibility = new IntersectionObserver(entries => {

        if (entries.some(entry => entry.isIntersecting)) loadMetadata();

      });

      this.visibility.observe(this);

      this.addEventListener('input', event => { if (event.target.name === 'discount') state.input = event.target.value; });

      const dialog = this.querySelector('dialog');

      dialog?.addEventListener('cancel', event => { event.preventDefault(); this.closePopup(); });

      dialog?.addEventListener('keydown', event => {

        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.closePopup(); }

      });

      dialog?.addEventListener('click', event => {

        if (event.target !== dialog) return;

        const rect = dialog.getBoundingClientRect();

        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.closePopup();

      });

      this.addEventListener('submit', event => {

        if (!event.target.matches('.zardo-discount-form')) return;

        event.preventDefault();

        this.updateCode(this.querySelector('[name="discount"]').value.trim());

      });

      this.addEventListener('click', event => {

        if (event.target.closest('[data-discount-open]')) this.openPopup();

        if (event.target.closest('[data-discount-close]')) this.closePopup();

        const button = event.target.closest('[data-discount-remove]');

        if (button) this.updateCode(button.dataset.discountRemove, true);

      });

    }

    openPopup() {

      state.open = this.querySelector('[data-discount-panel]') ? !state.open : true;

      renderAll();

      if(state.open){
        const panel=this.querySelector('[data-discount-panel]');
        const area=this.closest('#cart-drawer')?.querySelector('.zardo-cart-scroll-area');
        if(panel && area)requestAnimationFrame(()=>{
          const target=area.scrollTop+panel.getBoundingClientRect().top-area.getBoundingClientRect().top-Math.max(0,(area.clientHeight-panel.getBoundingClientRect().height)/2);
          area.scrollTo({top:Math.max(0,target),behavior:'smooth'});
        });
      }

      loadMetadata();

    }

    disconnectedCallback() { this.visibility?.disconnect(); }



    closePopup() {

      state.open = false;

      this.querySelector('dialog')?.close();
      renderAll();

      this.querySelector('[data-discount-open]').focus({ preventScroll: true });

    }

    renderSummary() {

      const summary = this.querySelector('[data-discount-summary]');



      let titles = [];

      try { titles = JSON.parse(summary?.dataset.discountTitles || '[]'); } catch (_) {}


      const popupSaving = this.querySelector('[data-popup-saving]');

      popupSaving.textContent = [titles.join(', '), summary?.querySelector('[data-discount-savings]')?.textContent].filter(Boolean).join(' \u00b7 ');

      popupSaving.hidden = !popupSaving.textContent;

    }

    syncFrom(doc) {

      const fresh = doc.querySelector('zardo-cart-discounts [data-discount-summary]');

      const current = this.querySelector('[data-discount-summary]');

      if (fresh && current && fresh.outerHTML !== current.outerHTML) current.replaceWith(fresh.cloneNode(true));

      this.renderState();

    }

    renderState() {

      const input = this.querySelector('[name="discount"]');

      if (!input) return;

      if (input.value !== state.input) input.value = state.input;

      this.renderSummary();

      const dialog = this.querySelector('dialog');

      const panel=this.querySelector('[data-discount-panel]');
      if(panel)panel.hidden=!state.open;
      else if(dialog){
        if(state.open && !dialog.open)dialog.showModal();
        else if(!state.open && dialog.open)dialog.close();
      }
      this.querySelector('[data-discount-open]')?.setAttribute('aria-expanded',String(state.open));

      const message = this.querySelector('.zardo-discount-status');

      message.textContent = state.message;

      message.toggleAttribute('data-error', state.error);

      this.toggleAttribute('aria-busy', state.busy);

      this.querySelectorAll('.zardo-discount-form input, .zardo-discount-form button').forEach(node => { node.disabled = state.busy; });

      const codes = this.querySelector('[data-discount-codes]');

      const signature = JSON.stringify(state.codes);

      if (codes.dataset.signature !== signature) {

        codes.replaceChildren(...state.codes.map(code => {

          const button = document.createElement('button');

          button.type = 'button'; button.dataset.discountRemove = code;

          button.textContent = code + ' ×'; button.setAttribute('aria-label', 'Remove discount ' + code);

          return button;

        }));

        codes.dataset.signature = signature;

      }

      codes.hidden = state.codes.length === 0;

      codes.querySelectorAll('button').forEach(node => { node.disabled = state.busy; });

    }

    message(text, error = false) {

      Object.assign(state, { message: text, error });

      renderAll();

    }

    async updateCode(code, remove = false) {

      if (state.busy || !code) return;

      if (code.includes(',')) { this.message('Please enter one discount code at a time.', true); return; }

      state.busy = true;

      this.message(remove ? 'Removing code…' : 'Checking code…');

      const root = window.Shopify?.routes?.root || '/';

      const sectionId = this.dataset.sectionId;

      const request = async codes => {

        const response = await fetch(root + 'cart/update.js', {

          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },

          body: JSON.stringify({ discount: codes.join(','), sections: [sectionId], sections_url: window.location.pathname })

        });

        const cart = await response.json();

        if (!response.ok) throw new Error(cart.description || 'Unable to update this discount. Please try again.');

        return cart;

      };

      let saved = false;

      try {

        const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });

        if (!response.ok) throw new Error('Unable to read your cart. Please try again.');

        const current = await response.json();

        const oldCodes = (current.discount_codes || []).map(item => item.code);

        const matches = value => value.toLowerCase() === code.toLowerCase();

        const nextCodes = oldCodes.filter(value => !matches(value));

        if (!remove) nextCodes.push(code);

        let cart = await request(nextCodes);

        const accepted = remove || (cart.discount_codes || []).some(item => matches(item.code) && item.applicable);

        // An ineligible code must not replace codes that were already on the cart.

        if (!accepted) cart = await request(oldCodes);

        saved = accepted;

        let html = cart.sections?.[sectionId];

        if (!html) {

          const rendered = await fetch(root + '?sections=' + encodeURIComponent(sectionId), { cache: 'no-store' });

          if (rendered.ok) html = (await rendered.json())[sectionId];

        }

        if (!html) throw new Error('The cart could not refresh. Please refresh the page to see your current discounts.');

        acceptCart(cart);

        this.syncFrom(new DOMParser().parseFromString(html, 'text/html'));

        document.dispatchEvent(new CustomEvent('zardo:discount-applied', { detail: { cart, html } }));

        if (accepted) {

          state.input = '';

          this.message(remove ? 'Discount code removed.' : 'Discount code applied.');

        } else {

          this.message('This code is invalid or cannot be combined with the offers in your cart.', true);

        }

      } catch (error) {

        this.message(saved ? 'Discount saved. Please refresh the page to update the cart display.' : (error.message || 'Unable to apply this code. Please try again.'), true);

      } finally {

        state.busy = false;

        renderAll();

      }

    }

  }

  customElements.define('zardo-cart-discounts', CartDiscounts);

})();

